import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Box, Card, Grid } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

interface ReportChartsProps {
  data: any;
}

type MonthlyChartRow = {
  month: string;
  claimsMadePerMonth: number;
  ordersPerMonth: number;
};

type RefundRow = {
  month: string;
  refund: number;
  refundSaved: number;
};

const ReportCharts: React.FC<ReportChartsProps> = () => {
  const theme = useTheme();

  const colorPalette = [
    theme.palette.primary.dark,
    theme.palette.primary.main,
    theme.palette.primary.light,
    theme.palette.secondary.dark,
  ];

  const [loading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<MonthlyChartRow[]>([]);
  const [refundData, setRefundData] = useState<RefundRow[]>([]);

  // currency formatter for CAD
  const currency = useMemo(
    () =>
      new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 2,
      }),
    []
  );

  const fetchData = async () => {
    try {
      const response = await fetch('/report/api/reportstats');
      const result = await response.json();

      // Build primary bar chart dataset: Orders vs Late Delivered
      const primary: MonthlyChartRow[] = (result?.ordersPerMonth ?? []).map(
        (item: any, index: number) => {
          const month = item?.month ?? '';
          const claimsMade =
            Number(
              (result?.claimsMadePerMonth ?? []).find(
                (obj: any) => obj?.month === month
              )?.total_claims
            ) || 0;

          return {
            claimsMadePerMonth: claimsMade,
            ordersPerMonth: Number(result?.ordersPerMonth?.[index]?.total_claims) || 0,
            month,
          };
        }
      );

      // Build second bar chart dataset: Refunds by month (+ Saved 40%)
   //   const refunds: RefundRow[] = (result?.totalRefund ?? []).map(
   //     (item: any) => {
   //       const refund = Number(item?.total_claims) || 0;
   //       const refundSaved = Number((refund * 0.4).toFixed(2));
   //       return {
   //         month: item?.month ?? '',
   //         refund,
   //         refundSaved,
   //       };
    //    }
    //  );
const refunds: RefundRow[] = (result?.totalRefund ?? []).map((item: any) => {
  const refund = Number(item?.total_refund) || 0;
  const refundSaved = Number((refund * 0.4).toFixed(2));

  return {
    month: item?.month ?? '',
    refund,
    refundSaved,
  };
});

      setChartData(primary);
      setRefundData(refunds);
    } catch (error) {
      console.error('Error fetching report stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const valueFormatterClaims = (value: number | null) =>
    `${value ?? 0} claims`;

  const valueFormatterCurrency = (value: number | null) =>
    currency.format((value ?? 0));

  const totalRefund = useMemo(
    () => refundData.reduce((acc, r) => acc + (r.refund || 0), 0),
    [refundData]
  );

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Grid container spacing={0} columns={12} sx={{ mb: 2 }}>
        {/* Bar Chart 1: Orders vs Late Delivered */}
        {/* <Grid item xs={12} sm={6} md={6}>
          <Typography
            sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '14pt' }}
            gutterBottom
          >
            Orders Vs Late Delivered
          </Typography>

          <BarChart
            dataset={chartData.filter((_, index) => index !== 0)} // exclude 1st month if desired
            xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
            series={[
              {
                dataKey: 'claimsMadePerMonth',
                label: 'Late Delivered',
                valueFormatter: valueFormatterClaims,
                color: colorPalette[0],
              },
              {
                dataKey: 'ordersPerMonth',
                label: 'Orders',
                valueFormatter: valueFormatterClaims,
                color: colorPalette[3],
              },
            ]}
            height={250}
            margin={{ left: 50, right: 0, top: 20, bottom: 20 }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { hidden: true },
            }}
          />
        </Grid> */}

        {/* Bar Chart 2: Refunds by Month (replacing Pie chart) */}
        <Grid item xs={12} sm={8} md={11}>
          <Typography
            sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '14pt' }}
            gutterBottom
          >
            Total Refund : {currency.format(totalRefund)}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarChart
              //dataset={refundData}
	      dataset={refundData.sort((a, b) => a.month.localeCompare(b.month))}
              xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
              series={[
                {
                  dataKey: 'refund',
                  
                  valueFormatter: valueFormatterCurrency,
                  color: colorPalette[1],
                },
             
              ]}
              height={250}
              margin={{ left: 50, right: 0, top: 20, bottom: 20 }}
              grid={{ horizontal: true }}
              slotProps={{
                legend: { hidden: false },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default ReportCharts;
